import { useQuery } from "@tanstack/react-query";
import { parseGeographyPoint, type GeoPoint } from "@bednbody/logic";
import { supabase } from "@/lib/supabase";
import type { StaffRole } from "@/lib/jwt-claims";

/**
 * The booking detail popup's own read.
 *
 * ═══ WHY THE POPUP NO LONGER RENDERS THE TABLE ROW ═══
 *
 * It used to be handed the row from the bookings table, whose feed selects a
 * dozen operational columns — so address, map, client, staff names and the
 * created time all rendered as "Unknown", "-" or a raw uuid, and the map read a
 * `POINT(...)` string PostgREST never sends (geography arrives as EWKB hex).
 *
 * ═══ THE DATA WALL DECIDES THE SELECT, NOT THE UI ═══
 *
 * `hub_manager` holds a COLUMN-LEVEL grant on `bookings` (0005) without
 * `client_id`, `address_text` or `geo`, and no grant on `profiles` (0003). Naming
 * any of those in its select is refused by the database, so the hub-manager
 * select below is the grant list, and the client/address/map fields are simply
 * absent for that role (`rules/data-wall.md`). `booking_events` is admin,
 * executive and superadmin only (0005).
 *
 * ⚠ SINCE 0285 THE MONEY COLUMNS SIT IN THAT SAME LIST. A hub manager has no
 * grant on gross_amount, giftpass_amount, convenience_fee or amount_due, so
 * naming one here would fail the WHOLE popup with a 42501 rather than hide a
 * figure. They are selected only for the roles that may see money.
 */

const OPERATIONAL = `id, ref, status, scheduled_at, hub_id, therapist_id, rider_id,
  payment_method,
  booking_group_id, cancelled_at, cancellation_stage, refund_status, created_at,
  hubs ( name, region, is_pilot ),
  service_variants ( duration_minutes, services ( name ) ),
  therapist:staff!bookings_therapist_id_fkey ( full_name ),
  rider:staff!bookings_rider_id_fkey ( full_name )`;

const WITH_MONEY = `${OPERATIONAL}, gross_amount, giftpass_amount, convenience_fee, amount_due`;

const WITH_CLIENT = `${WITH_MONEY},
  address_text, geo, notes_to_rider, completed_at, client_request_id,
  client:profiles!bookings_client_id_fkey ( full_name )`;

/** May this role see client name, address and pin? Hub managers never. */
export function canSeeClientDetails(role: StaffRole): boolean {
  return role !== "hub_manager";
}

/** May this role see what a booking is worth? Hub managers never (0285). */
export function canSeeBookingMoney(role: StaffRole): boolean {
  return role !== "hub_manager";
}

/** `booking_events` is readable by these three only (0005). */
export function canReadBookingEvents(role: StaffRole): boolean {
  return role === "admin" || role === "executive" || role === "superadmin";
}

type Named = { full_name: string | null } | { full_name: string | null }[] | null;

function nameOf(value: Named | undefined): string | null {
  const first = Array.isArray(value) ? value[0] : value;
  return first?.full_name ?? null;
}

export interface BookingDetailData {
  id: string;
  ref: string;
  status: string;
  scheduledAt: string;
  hubId: string | null;
  hubName: string | null;
  hubRegion: string | null;
  hubIsPilot: boolean;
  serviceName: string | null;
  durationMinutes: number | null;
  paymentMethod: string;
  /** All four are null for a hub manager — never selected (0285). */
  grossAmount: number | null;
  giftpassAmount: number | null;
  convenienceFee: number | null;
  amountDue: number | null;
  bookingGroupId: string | null;
  therapistId: string | null;
  therapistName: string | null;
  riderId: string | null;
  riderName: string | null;
  createdAt: string | null;
  cancelledAt: string | null;
  cancellationStage: string | null;
  refundStatus: string | null;
  /** Null for hub managers — never selected, see the header. */
  clientName: string | null;
  addressText: string | null;
  notesToRider: string | null;
  location: GeoPoint | null;
  completedAt: string | null;
  clientRequestId: string | null;
}

export function useBookingDetail(bookingId: string | null, role: StaffRole | null) {
  const withClient = role !== null && canSeeClientDetails(role);
  const withMoney = role !== null && canSeeBookingMoney(role);
  return useQuery({
    queryKey: ["booking-detail", bookingId, withClient, withMoney],
    enabled: bookingId !== null && role !== null,
    queryFn: async (): Promise<BookingDetailData> => {
      const { data, error } = await supabase
        .from("bookings")
        .select(withClient ? WITH_CLIENT : withMoney ? WITH_MONEY : OPERATIONAL)
        .eq("id", bookingId as string)
        .single();
      if (error) throw error;
      const r = data as unknown as Record<string, unknown>;
      const hub = (Array.isArray(r.hubs) ? r.hubs[0] : r.hubs) as {
        name?: string;
        region?: string | null;
        is_pilot?: boolean | null;
      } | null;
      const variant = r.service_variants as {
        duration_minutes?: number;
        services?: { name?: string } | { name?: string }[] | null;
      } | null;
      const service = Array.isArray(variant?.services) ? variant?.services[0] : variant?.services;
      return {
        id: r.id as string,
        ref: r.ref as string,
        status: r.status as string,
        scheduledAt: r.scheduled_at as string,
        hubId: (r.hub_id as string | null) ?? null,
        hubName: hub?.name ?? null,
        hubRegion: hub?.region ?? null,
        hubIsPilot: Boolean(hub?.is_pilot),
        serviceName: service?.name ?? null,
        durationMinutes: variant?.duration_minutes ?? null,
        paymentMethod: r.payment_method as string,
        grossAmount: withMoney ? Number(r.gross_amount ?? 0) : null,
        giftpassAmount: withMoney ? Number(r.giftpass_amount ?? 0) : null,
        convenienceFee: withMoney ? Number(r.convenience_fee ?? 0) : null,
        amountDue: withMoney ? Number(r.amount_due ?? 0) : null,
        bookingGroupId: (r.booking_group_id as string | null) ?? null,
        therapistId: (r.therapist_id as string | null) ?? null,
        therapistName: nameOf(r.therapist as Named),
        riderId: (r.rider_id as string | null) ?? null,
        riderName: nameOf(r.rider as Named),
        createdAt: (r.created_at as string | null) ?? null,
        cancelledAt: (r.cancelled_at as string | null) ?? null,
        cancellationStage: (r.cancellation_stage as string | null) ?? null,
        refundStatus: (r.refund_status as string | null) ?? null,
        clientName: withClient ? nameOf(r.client as Named) : null,
        addressText: withClient ? ((r.address_text as string | null) ?? null) : null,
        notesToRider: withClient ? ((r.notes_to_rider as string | null) ?? null) : null,
        location: withClient ? parseGeographyPoint(r.geo) : null,
        completedAt: withClient ? ((r.completed_at as string | null) ?? null) : null,
        clientRequestId: withClient ? ((r.client_request_id as string | null) ?? null) : null,
      };
    },
    // Opened to act on: a stale status would offer an override the server refuses.
    staleTime: 0,
    refetchInterval: 15_000,
  });
}

export interface BookingEventRow {
  id: string;
  fromStatus: string | null;
  toStatus: string;
  createdAt: string;
  action: string | null;
}

/** The real transition log — replaces a timeline that was guessed from `status`. */
export function useBookingEvents(bookingId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ["booking-detail-events", bookingId],
    enabled: enabled && bookingId !== null,
    queryFn: async (): Promise<BookingEventRow[]> => {
      const { data, error } = await supabase
        .from("booking_events")
        .select("id, from_status, to_status, created_at, meta")
        .eq("booking_id", bookingId as string)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []).map((e) => {
        const meta = e.meta as Record<string, unknown> | null;
        return {
          id: e.id,
          fromStatus: e.from_status ?? null,
          toStatus: e.to_status,
          createdAt: e.created_at ?? "",
          action: typeof meta?.action === "string" ? meta.action : null,
        };
      });
    },
    refetchInterval: 15_000,
  });
}
